import React from 'react'
import styled, { useTheme } from 'styled-components'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import { useRouter } from 'hooks/useRouter'
import { headerConfig } from 'config'
import { listingNotificationContracts } from 'config/listing-notification-contracts'
import { Flex, TextVariant, Text, useNavbar, Navbar, Tooltip } from '@mprolab/uikit'
import { HeaderItems } from 'config/types'
import isEmpty from 'lodash/isEmpty'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import UserPanel from './components/UserPanel'
import NotificationBell from './components/NotificationBell'

const NavbarContentWrapper = styled(Flex)`
  gap: 16px;
  width: 100%;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.tablet} {
    justify-content: flex-end;
    gap: 0;
  }
`

const DesktopWrapper = styled(Flex)`
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: flex-start;
`

const DesktopNavLinks = styled(Flex)`
  flex-wrap: nowrap;
  align-items: center;
  /*
   * No gap: the items carry their own padding and a rule stands between them.
   *
   * The row was 16px of gap on top of 16px of padding either side of every
   * item — 48px between one label and the next — which was comfortable at four
   * entries and ran into the wallet controls at seven. The separator does the
   * work the whitespace used to, in a third of the width.
   */
  gap: 0;
`

/**
 * A hairline between menu entries. Desktop only; the sidebar stacks.
 *
 * `gray8` rather than `gray7`: on this header's near-black ground the darker
 * grey is there in the DOM and invisible on a screen, which is the same as not
 * drawing it.
 */
const NavSeparator = styled(Flex)`
  flex: 0 0 1px;
  width: 1px;
  height: 14px;
  background: ${({ theme }) => theme.colors.gray8};
`

const MobileWrapper = styled(Flex)`
  flex-direction: column;
  width: 100%;
  gap: 16px;
  margin-top: 48px;
  padding: 0 8px;
`

const StyledMenuText = styled(Text)<{ disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ theme }) => theme.colors.gray3};

  transition: all 0.3s ease-in-out;
  ${({ disabled, theme }) =>
    !disabled &&
    `
    &:hover {
      color: ${theme.colors.gray1};
    }
  `}
`

const CellWrapper = styled(Flex)`
  padding: 4px 8px;
  border-radius: 32px;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.gray7};
`

const HeaderItemWrapper = styled(Flex)`
  padding: 0 12px;
  gap: 4px;
`

const Header: React.FC = () => {
  const theme = useTheme()
  const isTablet = useThemeMediaQuery('tablet')

  const { push, pathname } = useRouter()
  const { isOpen, onPresent, onDismiss } = useNavbar()
  const { account } = useWeb3React()

  const HeaderItem = ({ menuItem }: { menuItem: HeaderItems }) => {
    const { disabled, name, href, internalLink } = menuItem

    const handleClick = () => {
      if (href === '/' && pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else if (internalLink && !isEmpty(href)) {
        push(href, undefined, {
          shallow: true
        })
      } else if (!disabled && !isEmpty(href)) {
        window.open(href, '_blank')
      }
      onDismiss()
    }

    return (
      <HeaderItemWrapper onClick={handleClick}>
        <Tooltip title={disabled && !isTablet ? 'Coming soon' : ''}>
          <Flex gap="8px" alignItems="center">
            <StyledMenuText variant={TextVariant.BODY_SMALL} clickable={!disabled} {...{ disabled }}>
              {name}
            </StyledMenuText>
            {isTablet && disabled && (
              <CellWrapper>
                <Text variant={TextVariant.TEXT_XS_REGULAR}>Coming soon</Text>
              </CellWrapper>
            )}
          </Flex>
        </Tooltip>
      </HeaderItemWrapper>
    )
  }

  return (
    <Navbar
      {...{ isOpen, onPresent, onDismiss, contractsList: listingNotificationContracts, account }}
      sidebarContent={
        <MobileWrapper>
          {headerConfig.map((menuItem, index) => {
            return <HeaderItem key={index} {...{ menuItem }} />
          })}
        </MobileWrapper>
      }
      navbarContent={
        <NavbarContentWrapper>
          {!isTablet && (
            <DesktopWrapper>
              <DesktopNavLinks>
                {headerConfig.map((menuItem, index) => (
                  <React.Fragment key={menuItem.href ?? menuItem.name ?? index}>
                    {index > 0 && <NavSeparator />}
                    <HeaderItem {...{ menuItem }} />
                  </React.Fragment>
                ))}
              </DesktopNavLinks>
            </DesktopWrapper>
          )}
          <NotificationBell />
          <UserPanel />
        </NavbarContentWrapper>
      }
      onLogoClick={() => {
        if (pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' })
        else {
          push('/', undefined, {
            shallow: true
          })
        }
      }}
    />
  )
}

export default Header
